import React from "react";

import { useNavigate } from "react-router-dom";

import useIntegration from "@app/analytics/hooks/integrations/useIntegration";
import useIntegrations from "@app/analytics/hooks/integrations/useIntegrations";
import { INTEGRATION_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import { toast } from "@app/common/hooks/useToasts";
import type {
  IntegrationProvider,
  IntegrationProviderId,
  IntegrationSettingsKind,
} from "@app/common/types";

import { Link } from "@rui/Link";
import TooltipWrapper from "@rui/TooltipWrapper";

import { PushMenuItem } from "./PushMenuItem";
import { LogoIconButton, TooltipContent } from "./styles";
import type { ExternalShareProps } from "./types";

export interface PushToCrmProps extends ExternalShareProps {
  className?: string;
  providerId: IntegrationProviderId;
  pushIntegrationKind: IntegrationSettingsKind;
}

const DisabledTooltipTitle = ({
  provider,
  isConnected,
  isAutoPushEnabled,
}: {
  provider: IntegrationProvider;
  isConnected: boolean;
  isAutoPushEnabled: boolean;
}) => {
  if (!isConnected) {
    return (
      <TooltipContent>
        <Link color="shades.primary.200" to={provider.integrationUrl} underline>
          Enable {provider.displayName} integration
        </Link>{" "}
        to push meeting notes and contacts
      </TooltipContent>
    );
  }

  if (isAutoPushEnabled) {
    return (
      <>
        Reports are automatically pushed to {provider.displayName}. Pushing to{" "}
        {provider.displayName} may overwrite the existing meeting record.
      </>
    );
  }

  return <>Push report to {provider.displayName}</>;
};

const PushToCrm = ({
  className,
  isMenuItem,
  inEditMode,
  isProcessing,
  sessionId,
  providerId,
  pushIntegrationKind,
}: PushToCrmProps) => {
  const navigate = useNavigate();
  const { isProviderConnected, isSettingEnabled } = useIntegrations();
  const { sync } = useIntegration();

  const provider = INTEGRATION_PROVIDERS_BY_ID[providerId];

  const isIntegrationEnabled = isProviderConnected({
    providerId,
  });

  const isAutoPushEnabled = isSettingEnabled({
    providerId,
    integrationKind: pushIntegrationKind,
  });

  const handleSync = () => {
    sync.mutate(
      {
        integrationKind: pushIntegrationKind,
        intervalId: sessionId,
        messageKind: "session",
      },
      {
        onSuccess: () =>
          toast.success(
            `Your report will be pushed to ${provider.displayName}.`
          ),
        onError: () =>
          toast.error(
            `Could not push to ${provider.displayName}. Please try again later.`
          ),
      }
    );
  };

  const isDisabled = !isAutoPushEnabled || inEditMode || isProcessing;

  const iconButton = (
    <LogoIconButton
      alt={provider.displayName}
      iconSrc={provider.icon}
      onClick={handleSync}
      disabled={isDisabled}
    />
  );

  const menuItem = (
    <PushMenuItem
      disabled={isDisabled}
      displayName={provider.displayName}
      imgSrc={provider.icon}
      isConnected={isIntegrationEnabled}
      onClick={handleSync}
      onClickAdd={() => navigate(provider.integrationUrl)}
    />
  );

  return (
    <TooltipWrapper
      className={className}
      placement={isMenuItem ? "left" : "bottom"}
      show={!inEditMode}
      enterDelay={1500}
      title={
        <DisabledTooltipTitle
          isConnected={isIntegrationEnabled}
          isAutoPushEnabled={isAutoPushEnabled}
          provider={provider}
        />
      }
    >
      {isMenuItem ? menuItem : iconButton}
    </TooltipWrapper>
  );
};

export default PushToCrm;
